import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ValidationIndices } from './ShiplistConfirmationDialog';
import { useFieldArray, useForm, UseFormReturn } from 'react-hook-form';
import { DetailedShipList, ShipList } from '@ais-ui/api-spec/config';
import {
  shiplistMutateFormSchema,
  ShiplistMutateFormValues,
} from './formTypes';
import { zodResolver } from '@hookform/resolvers/zod';
import { grabShiplistInitialGroups } from '../../../Groups/grabInitialGroups';
import { useGetShareGroups } from '@ais-ui/websockets';

interface MutateShiplistContextProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmitting: boolean;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  totalShips: number;
  currentShips: any[];
  currentPage: number;
  itemsPerPage: number;
  AddGoToPage: (page: number) => void;
  goToPage: (page: number) => void;
  totalPages: number;
  fields: any[];
  append: (data: any) => void;
  remove: (index?: number | number[]) => void;
  update: (index: number, data: any) => void;
  action: 'create' | 'edit';
  mutate: (
    input: ShipList,
    callbacks: {
      onSuccess?: () => void;
      onError?: (error: { response?: any }) => void;
    }
  ) => void;
  shiplist: DetailedShipList | undefined;
  isLoading: boolean;
  invalidIndices: ValidationIndices;
  setInvalidIndices: React.Dispatch<React.SetStateAction<ValidationIndices>>;
  form: UseFormReturn<ShiplistMutateFormValues>;
}

const MutateShiplistContext = createContext<MutateShiplistContextProps | null>(
  null
);

export const useMutateShiplistContext = () => {
  const context = useContext(MutateShiplistContext);
  if (!context) {
    throw new Error(
      'useMutateShiplistContext must be used within a MutateShiplistProvider'
    );
  }
  return context;
};

interface MutateShiplistProviderProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  shiplist: DetailedShipList | undefined;
  action: 'create' | 'edit';
  mutate: (
    input: ShipList,
    callbacks: {
      onSuccess?: () => void;
      onError?: (error: { response?: any }) => void;
    }
  ) => void;
  children: ReactNode;
}

export const MutateShiplistProvider: React.FC<MutateShiplistProviderProps> = ({
  showDialog,
  setShowDialog,
  shiplist,
  action,
  mutate,
  children,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: groups, isLoading } = useGetShareGroups();

  const [invalidIndices, setInvalidIndices] = useState<ValidationIndices>({
    imo: [],
    mmsi: [],
  });

  const form = useForm<ShiplistMutateFormValues>({
    resolver: zodResolver(shiplistMutateFormSchema),
  });
  const { fields, append, remove, update, replace } = useFieldArray({
    control: form.control,
    name: 'ships',
  });

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (isInitialized) return;

    const resetValues = {
      name: shiplist?.name || '',
      color: shiplist?.color ? `#${shiplist.color}` : '#003b5c',
      selectedGroups: groups ? grabShiplistInitialGroups(shiplist, groups) : [],
      ships:
        shiplist?.ships && shiplist.ships.length > 0
          ? shiplist.ships.map((ship) => ({
              name: ship.name || '',
              mmsi: ship.mmsi,
              imo: ship.imo,
            }))
          : [{ name: '', mmsi: undefined, imo: undefined }],
    };

    form.reset(resetValues);
    setIsInitialized(true);
    replace(resetValues.ships);
  }, [shiplist, groups, isInitialized, form, replace]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const totalShips = useMemo(() => fields.length, [fields]);
  const totalPages = useMemo(
    () => Math.ceil(totalShips / itemsPerPage),
    [totalShips]
  );
  const currentShips = useMemo(() => {
    return fields.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  }, [fields, currentPage, itemsPerPage]);

  const goToPage = useCallback(
    (page: number) => {
      setCurrentPage((prevPage) => Math.max(1, Math.min(page, totalPages)));
    },
    [totalPages]
  );

  const AddGoToPage = useCallback(
    (page: number) => {
      setCurrentPage((prevPage) => Math.max(1, Math.max(page, totalPages)));
    },
    [totalPages]
  );

  const value = {
    showDialog,
    setShowDialog,
    isSubmitting,
    setIsSubmitting,
    totalShips,
    currentShips,
    currentPage,
    itemsPerPage,
    AddGoToPage,
    goToPage,
    totalPages,
    fields,
    append,
    remove,
    update,
    action,
    mutate,
    shiplist,
    isLoading,
    invalidIndices,
    setInvalidIndices,
    form,
  };

  if (!isInitialized) {
    return null; // Or return a loading state if you prefer
  }

  return (
    <MutateShiplistContext.Provider value={value}>
      {children}
    </MutateShiplistContext.Provider>
  );
};
